import {
  IOCAPIError,
  IQualificationCheckResult,
  IRunningTotalsResult,
  IViolationCache,
  IViolationResult,
  minuteInMilliseconds,
} from "@vmosolutions/oc-interfaces";
import _ from "lodash";
import moment from "moment-timezone";
import { batch } from "react-redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import {
  clearAllViolations,
  gotViolations,
  isLoadingCrewQualifications,
  isLoadingCrewViolations,
  loadedCrewViolations,
  setClearViolationsTimeoutId,
  updateCrewQualifications,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { ICrewViolations, ICrewViolationsMap } from "src/redux/reducers/CrewScheduleReducer";
import { findMosaicNodeByComponentName } from "src/utils/MosaicTree";
import AmplifyAPIWrapper from "./AmplifyAPIWrapper";

interface IQualificationResult {
  result: IQualificationCheckResult[];
  lastId: any;
}

/**
 * getQualifications - Retrieves the cached qualifications from database.
 *
 */
export const getQualifications = (crewIds?: string[]): ThunkAction<Promise<string>, object, object, any> => {
  return async (dispatch: ThunkDispatch<object, object, any>, getState: any): Promise<any> => {
    const isLoading = getState().crewSchedule.isLoadingCrewQualifications;
    if (isLoading) {
      return;
    }
    dispatch(isLoadingCrewQualifications(true));
    const allQualifications: IQualificationCheckResult[] = await fetchQualifications(crewIds);
    dispatch(updateCrewQualifications(allQualifications, crewIds));
    //set isLoadingCrewQualifications to false in the reducer
  };
};

export const fetchQualifications = async (crewIds?: string[]): Promise<IQualificationCheckResult[]> => {
  const allQualifications: IQualificationCheckResult[] = [];
  let fetchingQualifications = true;
  let id = "";
  const queryString = crewIds?.map((id) => `crewId=${id}`).join("&") ?? "";
  while (fetchingQualifications) {
    const quals: IQualificationResult | IOCAPIError | null = await AmplifyAPIWrapper.Get<IQualificationResult>(
      `/crew/quals/cache${id ? `/${id}` : ""}${queryString ? `?${queryString}` : ""}`
    );
    if (quals && Array.isArray(quals.result)) {
      allQualifications.push(...quals.result);
    }
    if (quals?.lastId) id = quals?.lastId;
    else {
      fetchingQualifications = false;
    }
  }
  return Array.isArray(allQualifications) ? allQualifications : [];
};

interface IViolationsResult {
  result: IViolationCache[];
  lastId: any;
}
/**
 * getViolations - Retrieves the cached violations from database.
 * NOTE: Does not rescan for violations
 */

export const getViolations = (crewIds?: string[]): ThunkAction<Promise<string>, object, object, any> => {
  return async (dispatch: ThunkDispatch<object, object, any>, getState: any): Promise<any> => {
    const rootState: IRootState = getState();
    const isLoading = rootState.crewSchedule.isLoadingCrewViolations;
    const initialLoad = rootState.crewSchedule.loadedViolations; //default false
    const userIsVMO = rootState.authentication?.attributes?.email?.includes("vmo.aero"); //for debugging purposes
    if (!initialLoad && isLoading && (!crewIds || crewIds?.length === 0)) {
      if (userIsVMO) {
        console.warn("did not load the initial vios yet - not calling another FETCH ALL VIOLATIONS");
      }
    }

    // if (!crewIds || crewIds?.length === 0) {
    //   return; // If no crew ids are provided, just return.
    // }
    // console.log("Websocket to say get crew's violations. Crew Id: ", crewIds);

    //is there a crew schedule window open or is there an errors window open?
    const currentNode = rootState.windowManager;
    const crewSchedWindowOpen = findMosaicNodeByComponentName(currentNode.currentNode, "CrewScheduleWindow");
    const crewViosdWindowOpen = findMosaicNodeByComponentName(
      currentNode.currentNode,
      "ErrorsWindow",
      "Crew Violations"
    );
    //if neither the crew sched window or the crew violations window is open AND you haven't intially loaded violations
    if (!crewSchedWindowOpen && !crewViosdWindowOpen) {
      const crewViolations = getState().crewSchedule.crewViolations;
      if (_.isEmpty(crewViolations) || !initialLoad) {
        if (userIsVMO) {
          console.warn(
            "There is no crew sched window open or an errors AND you haven't done an initial load of violations so not reacting to websocket updates"
          );
        }
        return;
      }
    }

    dispatch(isLoadingCrewViolations(true));
    const allViolations: any[] = [];
    let fetchingViolations = true;
    let queryString = "";

    crewIds?.forEach((crewId, index: number) => {
      queryString = queryString.concat(index > 0 ? `&crewId=${crewId}` : `crewId=${crewId}`);
    });

    let id = "";
    while (fetchingViolations) {
      // userIsVMO && console.log(`fetching violations ${queryString ? queryString : ""} ${id ? "another page" : ""}`);
      const violations: IViolationsResult | IOCAPIError | null = await AmplifyAPIWrapper.Get<IViolationsResult>(
        `/violations${id ? `/${id}` : ""}${queryString ? `?${queryString}` : ""}`
      );
      // there are now response that are violations = { results: [] }
      if (Array.isArray(violations?.result) && violations?.result.length) {
        allViolations.push(...violations.result);
      }
      if (violations?.lastId) id = violations?.lastId;
      else {
        fetchingViolations = false;
      }
    }

    //map all the fetched violations into a map that the front end can use
    const crewsViolationsInReduxFormat = allViolations.map((violation: IViolationCache) => {
      const violationMap = createCrewViolationMap(violation);
      return {
        violationMap,
        crewId: violation.crewId,
        createdAt: violation.createdAt,
      };
    });
    //send to redux
    batch(() =>
      crewsViolationsInReduxFormat.map((violation) => {
        return dispatch(gotViolations(violation.violationMap, violation.crewId, violation.createdAt?.valueOf()));
      })
    );
    //only set loaded to true if...
    if (initialLoad === false && (!crewIds || crewIds?.length === 0)) {
      dispatch(loadedCrewViolations(true));
    }
    dispatch(isLoadingCrewViolations(false));
  };
};

export const createCrewViolationMap = (re: IViolationCache): ICrewViolations => {
  //!Important if you change these values, update the function getRequiredSchedDataPeriod in TheBusiness.ts
  const start = moment.utc(re.start).startOf("month"); //start usuable
  const end = moment.utc(re.end).endOf("month"); //end usuable

  const crewVios: ICrewViolations = {};
  //any violations that are in fall is this range are considered "usable"
  //expecting that we have all the violations for these months unless there is a
  // discontinuity on a crew's schedule that is causing the violations to stop calculating...
  const initialTime = moment.utc(start);
  for (let s = initialTime.clone(); s.isSameOrBefore(end); s.add(1, "month")) {
    const startTime = s?.valueOf();
    const endTime = moment.utc(s).endOf("month")?.valueOf();
    const dateKey = s.format("YYYYMM");
    type StringKeyMap = { [key: string]: any };
    //add to  the map
    crewVios[dateKey] = {
      regulationResults: re.results.filter((res) =>
        s.isSame(initialTime)
          ? startTime <= Math.max(startTime, res.period.start) && res.period.end > startTime
          : startTime <= res.period.start && res.period.start <= endTime
      ),
      runningTotalResults: re.runningTotalResults.map((res: IRunningTotalsResult) => {
        const map = res.map as StringKeyMap;
        const allKeys = Object.keys(map);
        const filteredMap: StringKeyMap = {};
        allKeys.forEach((key: string) => {
          if (moment(key, "YYYY-MM-DD")?.valueOf() >= startTime && moment(key, "YYYY-MM-DD")?.valueOf() <= endTime) {
            filteredMap[key] = map[key];
          }
        });
        return { type: res.type, map: filteredMap };
      }),
      lastModified: new Date()?.valueOf(),
      createdAt: re.createdAt?.valueOf(),
    };
  }

  //lets check for any discontinuity errors that occur between the start, startUsable - 1 months period
  //ignore any violations that are NOT discontinuity because those are only a subset of violations for
  // this period
  for (
    let s = moment.utc(start).subtract(1, "year");
    s.isSameOrBefore(moment.utc(start).subtract(1, "month"));
    s.add(1, "month")
  ) {
    const startTime = s?.valueOf();
    const endTime = moment.utc(s).endOf("month")?.valueOf();

    const dateKey = s.format("YYYYMM");

    //add to the map only if there is something for that month
    const regs: {
      regulationResults: IViolationResult[];
      runningTotalResults: IRunningTotalsResult[];
      lastModified?: number;
      createdAt?: number;
    } = {
      regulationResults: re.results,
      runningTotalResults: [], //There wont be any running totals here because on the backend I have filtered out any that a more than 1 month
      lastModified: new Date()?.valueOf(),
      createdAt: re.createdAt?.valueOf(),
    };
    //only add the is if there are violations for this month
    if (regs.regulationResults.length > 0) {
      crewVios[dateKey] = regs;
    }
  }
  return crewVios;
};

/**
 *Checks if there are any crew schedule windows open or crew error windows open
 if there aren't, clears out what is in redux for violations - the app will no longer respond to the ws updates
 for a crew's violations being recalculated
 */
export const clearViolationsFromRedux = (): ThunkAction<object, object, object, any> => {
  return (dispatch: ThunkDispatch<object, object, any>, getState: any): any => {
    const userIsVMO = getState().authentication?.attributes?.email?.includes("vmo.aero"); //for debugging purposes

    const timeoutId = setTimeout(() => {
      const currentNode = getState().windowManager;
      const crewSchedWindowOpen = findMosaicNodeByComponentName(currentNode.currentNode, "CrewScheduleWindow");
      const crewViosdWindowOpen = findMosaicNodeByComponentName(
        currentNode.currentNode,
        "ErrorsWindow",
        "Crew Violations"
      );
      //This !crewSchedWindowOpen && !crewViosdWindowOpen check should prevent it from clearing out redux by mistake
      if (!crewSchedWindowOpen && !crewViosdWindowOpen) {
        if (userIsVMO) {
          console.warn(
            "Still no crew sched or an errors window open so lets clear up redux and stop reacting to websocket updates"
          );
        }
        //clear states in redux
        dispatch(isLoadingCrewViolations(false));
        dispatch(loadedCrewViolations(false));
        dispatch(clearAllViolations());
      } else {
        if (userIsVMO) {
          console.warn("You reopened a crew sched or an errors window so we wont clear the violations out of redux");
        }
      }
    }, 5 * minuteInMilliseconds);

    //store this timeout id in redux
    dispatch(setClearViolationsTimeoutId(timeoutId));
  };
};
